/**
 * Fetch utility with timeout support and error handling
 */

import { removeItem } from '@/db/services/application_session_service';
import { clearDevice, getDeviceToken } from '@/db/services/device_service';
import { touchLastOnlineTime } from '@/db/services/offline_time_tracker';
import { getConnectivityEra, markServerUnreachable } from './connectivity';

export interface FetchWithTimeoutOptions extends RequestInit {
  timeout?: number;
}

export class NetworkError extends Error {
  constructor(message: string, public cause?: Error) {
    super(message);
    this.name = 'NetworkError';
  }
}

export class TimeoutError extends Error {
  constructor(timeout: number) {
    super(`Request timed out after ${timeout}ms`);
    this.name = 'TimeoutError';
  }
}

/**
 * Fetch with timeout support
 * @param url - Request URL
 * @param options - Fetch options with optional timeout
 * @returns Promise<Response>
 */
export async function fetchWithTimeout(
  url: string | URL,
  options: FetchWithTimeoutOptions = {}
): Promise<Response> {
  const { timeout = 10000, signal: externalSignal, ...fetchOptions } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  // Honor a caller-supplied signal alongside the internal timeout controller,
  // so callers can cancel an in-flight request (e.g. navigating away from a
  // PDF that is still render-polling). A caller abort surfaces as the original
  // AbortError, not a TimeoutError.
  if (externalSignal?.aborted) {
    clearTimeout(timeoutId);
    throw new DOMException('Aborted', 'AbortError');
  }
  const onExternalAbort = () => controller.abort();
  externalSignal?.addEventListener('abort', onExternalAbort, { once: true });

  // Snapshot the connectivity era at request start: if this fetch fails after
  // the era moved on (test isolation reset), the failure is stale context and
  // must not flip the current connectivity state.
  const era = getConnectivityEra();

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
      // Always include credentials (cookies) for session-based authentication
      credentials: fetchOptions.credentials || 'include',
      headers: {
        // Suppress browser's native HTTP Basic Auth dialog on 401 responses.
        // Without this, Chrome pops a "Sign In" modal when the session expires.
        'X-Requested-With': 'XMLHttpRequest',
        ...(fetchOptions.headers as Record<string, string> || {}),
      },
    });

    clearTimeout(timeoutId);
    externalSignal?.removeEventListener('abort', onExternalAbort);
    // Any HTTP response — even an error status — proves the server was
    // reachable, which is what the offline time limit actually measures.
    // Relies on the service worker's api-cache staying effectively disabled
    // (see the INVARIANT note in src/sw.ts, guarded by src/sw.test.ts) so a
    // cache-served response can't spoof reachability while offline.
    // Fire-and-forget: touchLastOnlineTime throttles and never throws.
    void touchLastOnlineTime();
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    externalSignal?.removeEventListener('abort', onExternalAbort);

    // The caller aborted (vs. our timeout controller firing) — propagate the
    // abort as-is so it isn't misreported as a timeout or network failure.
    // Checked before the instanceof branch: the AbortError is a DOMException,
    // which is not instanceof Error in every runtime.
    if (externalSignal?.aborted) {
      throw error;
    }

    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new TimeoutError(timeout);
      }
      // Connection refused, DNS failure, etc. — the server is unreachable.
      markServerUnreachable(era);
      throw new NetworkError(`Network request failed: ${error.message}`, error);
    }

    markServerUnreachable(era);
    throw new NetworkError('Unknown network error');
  }
}

/**
 * Fetch with timeout and retry logic
 * @param url - Request URL
 * @param options - Fetch options
 * @param maxRetries - Maximum number of retries (default: 1)
 * @param retryDelay - Delay between retries in ms (default: 1000)
 * @returns Promise<Response>
 */
export async function fetchWithRetry(
  url: string | URL,
  options: FetchWithTimeoutOptions = {},
  maxRetries: number = 1,
  retryDelay: number = 1000
): Promise<Response> {
  let lastError: Error = new Error('No attempts made');

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetchWithTimeout(url, options);

      // If response is ok, return it
      if (response.ok) {
        return response;
      }

      // For non-ok responses, don't retry on client errors (4xx)
      if (response.status >= 400 && response.status < 500) {
        return response;
      }

      // For server errors (5xx), retry if we have attempts left
      if (attempt === maxRetries) {
        return response;
      }

      lastError = new Error(`HTTP ${response.status}: ${response.statusText}`);
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');

      // Don't retry on timeout errors or if this is the last attempt
      if (error instanceof TimeoutError || attempt === maxRetries) {
        throw lastError;
      }
    }

    // Wait before retrying (with exponential backoff)
    if (attempt < maxRetries) {
      await new Promise(resolve => setTimeout(resolve, retryDelay * Math.pow(2, attempt)));
    }
  }

  throw lastError;
}

/**
 * Error thrown when authentication is expired
 */
export class AuthExpiredError extends Error {
  constructor() {
    super('Authentication expired. Please log in again.');
    this.name = 'AuthExpiredError';
  }
}

/**
 * Fetch with automatic auth expiration handling
 *
 * Authentication mode is chosen per request:
 *   - Device token present (Mon Ami GO has registered with the server) →
 *     send `Authorization: Bearer <token>` and omit credentials.
 *   - No device token (transitional or pre-registration) → fall back to
 *     `credentials: 'include'` so the Rails session cookie carries auth.
 *
 * @param url - Request URL
 * @param options - Fetch options
 * @param onAuthExpired - Optional callback for auth expiration
 * @returns Promise<Response>
 */
export async function fetchWithAuth(
  url: string | URL,
  options: FetchWithTimeoutOptions = {},
  onAuthExpired?: () => void
): Promise<Response> {
  const token = await getDeviceToken();

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> || {}),
  };
  let mergedOptions: FetchWithTimeoutOptions;
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    mergedOptions = { ...options, headers, credentials: 'omit' };
  } else {
    mergedOptions = { ...options, headers, credentials: options.credentials ?? 'include' };
  }

  const response = await fetchWithTimeout(url, mergedOptions);

  // 401 Unauthorized = session/token expired → clear credentials, trigger
  // the auth-expired callback. 403 Forbidden is NOT treated as auth expiry —
  // it means the user is authenticated but lacks permission for this
  // specific action (e.g., editing a completed document). Treating 403 as
  // session expiry caused spurious logouts when the server rejected writes
  // on immutable resources.
  if (response.status === 401) {
    // Note: raw strings here instead of CURRENT_USER_ID_KEY / CURRENT_PERSON_KEY
    // to avoid a circular dependency (user_service → fetch-utils → user_service).
    // Every clear is wrapped so a transient DB failure (e.g., the locked-DB
    // window during unlock) can't replace the AuthExpiredError signal with
    // a Dexie exception — callers branch on `error instanceof AuthExpiredError`,
    // and the auth-expired path is the load-bearing outcome here.
    try { await removeItem('current_user_id'); } catch { /* swallow */ }
    try { await removeItem('current_person'); } catch { /* swallow */ }
    // `current_user` matches the cleanup in fetchCurrentUser's 401 path and
    // softSignOut so useSessionValidator's mount-time Sentry rehydration
    // can't re-attach the prior identity to the /login redirect.
    try { await removeItem('current_user'); } catch { /* swallow */ }
    // Drop the local device token too — whatever the reason field on the
    // response body, the token is no longer accepted (revoked, expired, or
    // unknown), so keeping it would just produce more 401s. The user goes
    // through /login → /auth/callback → POST /devices to get a new one.
    if (token) {
      try { await clearDevice(); } catch { /* swallow */ }
    }

    if (onAuthExpired) {
      onAuthExpired();
    }
    throw new AuthExpiredError();
  }

  return response;
}
